import { getImageValidationError } from '../lib/image-validation'
import { getSupabaseClient } from '../lib/supabase'

const IMAGE_BUCKET = 'company-images'

const extensionByMimeType: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

export interface StoredImageMetadata {
  storage_path: string
  original_name: string
  mime_type: string
  size_bytes: number
}

export interface UploadedCompanyImage extends StoredImageMetadata {
  public_url: string
}

/** Único adaptador de archivos: la interfaz no conoce APIs de Supabase Storage. */
export const imageService = {
  async uploadCompanyImage(companyId: string, file: File): Promise<UploadedCompanyImage> {
    const validationError = getImageValidationError(file)
    if (validationError) throw new Error(validationError)

    const extension = extensionByMimeType[file.type]
    if (!extension) throw new Error('La imagen debe ser JPG, JPEG, PNG o WEBP.')

    const storagePath = `${companyId}/${crypto.randomUUID()}.${extension}`
    const { error } = await getSupabaseClient()
      .storage.from(IMAGE_BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        contentType: file.type,
        upsert: false,
      })

    if (error) {
      throw new Error('No fue posible subir la imagen. Revisa tu conexión e inténtalo de nuevo.')
    }

    return {
      storage_path: storagePath,
      original_name: file.name,
      mime_type: file.type,
      size_bytes: file.size,
      public_url: this.getPublicUrl(storagePath),
    }
  },

  async removeCompanyImage(storagePath: string): Promise<void> {
    const { data, error } = await getSupabaseClient()
      .storage.from(IMAGE_BUCKET)
      .remove([storagePath])
    if (error) throw new Error('Supabase Storage no pudo eliminar la imagen.')

    const removedPaths = new Set(data?.map(({ name }) => name) ?? [])
    if (data && data.length > 0 && !removedPaths.has(storagePath)) {
      throw new Error('Supabase Storage no confirmó la eliminación de la imagen.')
    }
  },

  getPublicUrl(storagePath: string): string {
    return getSupabaseClient().storage.from(IMAGE_BUCKET).getPublicUrl(storagePath).data.publicUrl
  },
}
